import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer
} from 'recharts';

const RE_YM = /^\d{4}-\d{2}$/;   // 2025-07
const RE_YMD = /^\d{4}-\d{2}-\d{2}/; // 2025-07-15
const RE_DM = /^\d{2}\/\d{2}$/;  // 07/07
const RE_YM_COMPACT = /^\d{6}$/; // 202507
const RE_MY = /^\d{2}-\d{4}$/;   // 07-2025
const RE_YM_SLASH = /^\d{4}\/\d{2}$/; // 2025/07
const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const MONTHS_SHORT_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const COLOR_TEXT = 'rgb(var(--text))';
const COLOR_MUTED = 'rgb(var(--muted))';
const COLOR_GRID = 'rgb(var(--border))';
const COLOR_SUCCESS = 'rgb(var(--success))';
const COLOR_DANGER = 'rgb(var(--danger))';
const NOOP = () => {};

const pickPeriodValue = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return (
      value.name ??
      value.mes ??
      value.month ??
      value.period ??
      value.monthKey ??
      value.fecha ??
      value.date ??
      value.label ??
      value.key ??
      ''
    );
  }
  return value;
};

const normalizePeriodKey = (value) => {
  const raw = pickPeriodValue(value);
  if (!raw) return '';
  if (raw instanceof Date) return raw.toISOString().slice(0, 7);
  const s = String(raw);
  if (RE_YM.test(s)) return s;
  if (RE_YMD.test(s)) return s.slice(0, 7);
  if (RE_YM_COMPACT.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}`;
  if (RE_YM_SLASH.test(s)) return s.replace('/', '-');
  if (RE_MY.test(s)) {
    const [m, y] = s.split('-');
    return `${y}-${m}`;
  }
  return s;
};

const getMonthLabel = (value, short = false, fallback = '') => {
  const key = normalizePeriodKey(value);
  if (RE_YM.test(key)) {
    const monthIndex = parseInt(key.slice(5, 7), 10) - 1;
    const list = short ? MONTHS_SHORT_ES : MONTHS_ES;
    return list[monthIndex] ?? fallback ?? key;
  }
  const raw = pickPeriodValue(value);
  if (typeof raw === 'string' && raw) return raw;
  return fallback || String(key ?? '');
};

const formatYAxisValue = (value) => {
  if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + 'M';
  if (value >= 1_000) return (value / 1_000).toFixed(0) + 'K';
  return Number(value).toLocaleString('es-CL');
};

const parseAmount = (value) => {
  if (typeof value === 'number') return value;
  if (value == null) return 0;
  const direct = Number(value);
  if (Number.isFinite(direct)) return direct;
  const sanitized = String(value)
    .replace(/\s+/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const fallback = Number(sanitized);
  return Number.isFinite(fallback) ? fallback : 0;
};
function chronoCompare(a, b) {
  // Meses YYYY-MM -> string compare funciona porque está zero-padded
  if (RE_YM.test(a) && RE_YM.test(b)) return a.localeCompare(b);

  // Días del mes DD/MM -> comparamos mes y luego día (ascendente)
  if (RE_DM.test(a) && RE_DM.test(b)) {
    const [da, ma] = a.split('/').map(Number);
    const [db, mb] = b.split('/').map(Number);
    if (ma !== mb) return ma - mb;
    return da - db;
  }

  // Intento general con Date.parse
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (!Number.isNaN(ta) && !Number.isNaN(tb)) return ta - tb;

  // Fallback lexicográfico con soporte numérico
  return a.localeCompare(b, 'es-ES', { numeric: true, sensitivity: 'base' });
}

function transformSummaryDataToChartData(summaryData) {
  const normalizeSeries = (series = {}) => {
    const out = {};
    Object.entries(series).forEach(([key, value]) => {
      const normalized = normalizePeriodKey(key);
      if (!normalized) return;
      const num = parseAmount(value);
      out[normalized] = (out[normalized] ?? 0) + num;
    });
    return out;
  };

  const ingresos = normalizeSeries(summaryData?.ingresos);
  const gastos = normalizeSeries(summaryData?.gastos);
  const total = normalizeSeries(summaryData?.total);

  const dataKeys = new Set([
    ...Object.keys(ingresos),
    ...Object.keys(gastos),
    ...Object.keys(total),
  ]);

  let periods = [];
  const uniquePeriods = Array.isArray(summaryData?.uniquePeriods)
    ? summaryData.uniquePeriods.map(normalizePeriodKey).filter(Boolean)
    : [];

  if (uniquePeriods.length) {
    const uniqueSet = new Set(uniquePeriods);
    const keysMatch =
      uniqueSet.size === dataKeys.size &&
      [...uniqueSet].every(key => dataKeys.has(key));
    periods = keysMatch ? uniquePeriods : [...dataKeys].sort(chronoCompare);
  } else {
    periods = [...dataKeys].sort(chronoCompare);
  }

  return periods.map(period => {
    const nameKey = normalizePeriodKey(period);
    const ingresosVal = ingresos[nameKey] ?? 0;
    const gastosVal = gastos[nameKey] ?? 0;
    const totalVal = total[nameKey] ?? (ingresosVal - gastosVal);

    return {
      name: nameKey,
      label: getMonthLabel(nameKey, false, nameKey),
      labelShort: getMonthLabel(nameKey, true, nameKey),
      ingresos: Math.abs(ingresosVal),
      ingresosOriginal: ingresosVal,
      gastos: Math.abs(gastosVal),
      gastosOriginal: gastosVal,
      total: Math.abs(totalVal),
      totalOriginal: totalVal,
    };
  });
}

const BarChartER = ({ summaryData, onSelectMonth = NOOP, visibleCount }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const lastHoveredPeriodRef = useRef(null);

  const baseChartData = useMemo(() => transformSummaryDataToChartData(summaryData), [summaryData]);
  const resolvedVisibleCount = useMemo(() => {
    if (Number.isFinite(visibleCount) && visibleCount > 0) return visibleCount;
    return isMobile ? 3 : 12;
  }, [isMobile, visibleCount]);
  const chartData = useMemo(() => {
    return baseChartData.slice(-resolvedVisibleCount);
  }, [baseChartData, resolvedVisibleCount]);
  const chartLayout = 'horizontal';
  const heightDesktop = 345;
  const chartHeight = isMobile ? 260 : heightDesktop;
  const mobileBarSize = 28;
  const valueLabelFontSize = chartData.length >= 8 ? 11 : 12;


  // Autoselecciona el último período (el más reciente) sin abrir modal
  useEffect(() => {
    if (chartData.length > 0) {
      const last = chartData[chartData.length - 1];
      lastHoveredPeriodRef.current = last.name;
      onSelectMonth({ name: last.name, data: last, openModal: false });
    }
  }, [chartData, onSelectMonth]);

  const handleMouseEnter = useCallback((payload) => {
    if (!payload?.name) return;
    if (lastHoveredPeriodRef.current === payload.name) return;
    lastHoveredPeriodRef.current = payload.name;
    onSelectMonth({ name: payload.name, data: payload, openModal: false });
  }, [onSelectMonth]);

  const renderIngresosLabel = (props) => {
    if (!props?.payload) return null;
    const { x, y, width, payload } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill={COLOR_TEXT}
        textAnchor="middle"
        fontSize={valueLabelFontSize}
      >
        {Number(payload.ingresosOriginal ?? 0).toLocaleString('es-CL')}
      </text>
    );
  };

  const renderGastosLabel = (props) => {
    if (!props?.payload) return null;
    const { x, y, width, payload } = props;
    return (
      <text
        x={x + width / 2}
        y={y - 5}
        fill={COLOR_TEXT}
        textAnchor="middle"
        fontSize={valueLabelFontSize}
      >
        {Number(payload.gastosOriginal ?? 0).toLocaleString('es-CL')}
      </text>
    );
  };

  const renderMonthTick = (props) => {
    const { x, y, index, payload } = props || {};
    const label =
      chartData?.[index]?.labelShort ||
      chartData?.[index]?.label ||
      getMonthLabel(payload?.value, true, payload?.value);
    if (!label) return null;
    if (!isMobile) {
      return (
        <text
          x={x}
          y={(y ?? 0) + 12}
          textAnchor="middle"
          fill={COLOR_MUTED}
          fontSize={chartData.length >= 8 ? 11 : 12}
        >
          {label}
        </text>
      );
    }
    return (
      <text
        x={x}
        y={(y ?? 0) + 18}
        textAnchor="middle"
        fill={COLOR_MUTED}
        fontSize={11}
        transform={`rotate(-90, ${x}, ${(y ?? 0) + 18})`}
      >
        {label}
      </text>
    );
  };

  const showBottomLabels = !isMobile && chartLayout === 'horizontal' && chartData.length > 0;
  const showMobileLabels = isMobile && chartData.length > 0;
  return (
    <div
      className="w-full"
      style={isMobile ? { height: chartHeight } : undefined}
    >
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={chartData}
          layout={chartLayout}
          margin={isMobile ? { top: 12, right: 8, left: 8, bottom: 28 } : { top: 20, right: 0, left: 0, bottom: 24 }}
          barSize={isMobile ? mobileBarSize : 40}
          barGap={2}
          onClick={(e) => {
            if (e?.activePayload?.length) {
              const payload = e.activePayload[0].payload;
              lastHoveredPeriodRef.current = payload.name;
              onSelectMonth({ name: payload.name, data: payload, openModal: true });
            }
          }}
        >
          <CartesianGrid strokeDasharray="1 1" vertical={false} stroke={COLOR_GRID} />

          <>
            <XAxis
              type="category"
              dataKey="name"
              ticks={chartData.map(item => item.name)}
              tick={isMobile ? false : renderMonthTick}
              axisLine={false}
              tickLine={false}
              height={isMobile ? 8 : 8}
              tickMargin={0}
              interval={0}
              minTickGap={0}
              allowDuplicatedCategory={false}
            />
            <YAxis
              type="number"
              width={isMobile ? 44 : 52}
              tick={({ x, y, payload }) => (
                <text
                  x={x - 6}
                  y={y + 4}
                  textAnchor="end"
                  fill={COLOR_MUTED}
                  fontSize={isMobile ? 11 : 12}
                >
                  {formatYAxisValue(payload?.value ?? 0)}
                </text>
              )}
              axisLine={false}
              tickLine={false}
            />
          </>

          <Legend content={() => null} />

          <Bar
            dataKey="ingresos"
            fill={COLOR_SUCCESS}
            label={renderIngresosLabel}
            minPointSize={2}
            radius={isMobile ? [0, 8, 8, 0] : [10, 10, 0, 0]}
            onMouseEnter={(e) => {
              handleMouseEnter(e?.payload);
            }}
          />
          <Bar
            dataKey="gastos"
            fill={COLOR_DANGER}
            label={renderGastosLabel}
            minPointSize={2}
            radius={isMobile ? [0, 8, 8, 0] : [10, 10, 0, 0]}
            onMouseEnter={(e) => {
              handleMouseEnter(e?.payload);
            }}
          />
        </BarChart>
      </ResponsiveContainer>
      {showBottomLabels && (
        <div
          className="mt-2 grid text-xs text-muted"
          style={{ gridTemplateColumns: `repeat(${chartData.length}, minmax(0, 1fr))` }}
        >
          {chartData.map(item => (
            <div key={item.name} className="text-center">
              {item.labelShort}
            </div>
          ))}
        </div>
      )}
      {showMobileLabels && (
        <div
          className="mt-2 grid text-xs text-muted"
          style={{ gridTemplateColumns: `repeat(${chartData.length}, minmax(0, 1fr))` }}
        >
          {chartData.map(item => (
            <div key={item.name} className="text-center">
              {item.labelShort || item.label || getMonthLabel(item.name, true, item.name)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BarChartER;
